/**
 * Corpse-investigation behavior: scan nearby corpses, gate on LOS, and manage the
 * blacklist of already-investigated positions. Extracted from AIComponent so the
 * corpse-detection logic lives beside the other duty-tier behavior modules rather
 * than inside the top-level orchestrator.
 *
 * Flow:
 *   1. `checkNearbyCorpses` is called each duty tick with the current corpse list.
 *      - If a corpse is within INVESTIGATE_RADIUS and NOT on the blacklist:
 *        - LOS clear  → return `{ action: 'spike', x, y }` (AIComponent calls spikeAggro)
 *        - LOS blocked → return `{ action: 'investigate', x, y }` (AIComponent calls
 *                         DutySystem.setInvestigateTarget)
 *   2. `resolveArrival` is called when DutySystem.investigateArrived flips true.
 *      - LOS now available → return `{ action: 'spike', x, y }`
 *      - Still no LOS      → blacklist the target, return `{ action: 'resume' }`
 *   3. `resetBlacklist` is called on hard AI reset (player death / wipe).
 */
import { distanceSq } from '../../utils/Math';
import type { CorpseRecord } from '../../systems/EnemyManager';
import type { Detection } from '../Detection';
import { INVESTIGATE_RADIUS } from '../constants';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Mutable corpse-investigation state owned by AIComponent. */
export interface CorpseInvestigationState {
  /** Positions already investigated — prevents infinite re-investigation loops. Key format: "x,y". */
  blacklist: Set<string>;
}

/** Create a fresh, empty investigation state. */
export function createCorpseInvestigationState(): CorpseInvestigationState {
  return { blacklist: new Set() };
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/**
 * The action AIComponent must take in response to a corpse check.
 *
 * - `'spike'`       — call `spikeAggro(x, y)`. x/y are the corpse world coordinates.
 * - `'investigate'` — call `DutySystem.setInvestigateTarget(x, y)`. x/y are the corpse world coordinates.
 * - `'none'`        — no new action; continue normal duty behavior.
 */
export type CorpseCheckAction =
  | { action: 'spike'; x: number; y: number }
  | { action: 'investigate'; x: number; y: number }
  | { action: 'none' };

/**
 * The action AIComponent must take once the entity arrives at the investigate target.
 *
 * - `'spike'`  — corpse is now visible; call `spikeAggro(x, y)`.
 * - `'resume'` — no LOS; blacklist the corpse and resume patrol.
 */
export type CorpseArrivalAction =
  | { action: 'spike'; x: number; y: number }
  | { action: 'resume'; x: number; y: number };

// ---------------------------------------------------------------------------
// Behavior functions
// ---------------------------------------------------------------------------

/**
 * Scan nearby corpses and decide the appropriate action for this duty tick.
 *
 * Must only be called when the entity is in `duty` state and NOT already in
 * the `investigate` substate — the caller is responsible for that guard (it
 * already lives inside AIComponent's `checkNearbyCorpses` call site).
 *
 * @param state         Mutable investigation state (blacklist).
 * @param entityX       World X of the entity.
 * @param entityY       World Y of the entity.
 * @param corpses       Active corpse records from EnemyManager.
 * @param detection     Detection subsystem used to check LOS to corpse positions.
 * @param logs          Accumulated log messages (mutated in place).
 * @returns The action AIComponent should execute, or `{ action: 'none' }`.
 */
export function checkNearbyCorpses(
  state: CorpseInvestigationState,
  entityX: number,
  entityY: number,
  corpses: readonly CorpseRecord[],
  detection: Detection,
  logs: string[],
): CorpseCheckAction {
  if (corpses.length === 0) return { action: 'none' };

  const radiusSq = INVESTIGATE_RADIUS * INVESTIGATE_RADIUS;

  for (const corpse of corpses) {
    if (distanceSq(entityX, entityY, corpse.x, corpse.y) > radiusSq) continue;

    const key = corpseKey(corpse.x, corpse.y);
    if (state.blacklist.has(key)) {
      logs.push(`CORPSE skip blacklisted at (${Math.round(corpse.x)},${Math.round(corpse.y)})`);
      continue;
    }

    if (detection.checkLOS(entityX, entityY, corpse.x, corpse.y)) {
      logs.push(`CORPSE spotted at (${Math.round(corpse.x)},${Math.round(corpse.y)}) → spike aggro`);
      return { action: 'spike', x: corpse.x, y: corpse.y };
    }

    // In range but LOS blocked — begin investigation
    logs.push(`CORPSE sensed at (${Math.round(corpse.x)},${Math.round(corpse.y)}) → investigate`);
    return { action: 'investigate', x: corpse.x, y: corpse.y };
  }

  return { action: 'none' };
}

/**
 * Resolve what to do once the entity has arrived near the investigate target.
 *
 * If LOS is now available, the corpse is visible and combat should begin.
 * If LOS is still blocked (e.g. the path stopped short of a wall), blacklist
 * the target so the entity does not enter an infinite investigate loop.
 *
 * @param state     Mutable investigation state (blacklist is updated on no-LOS).
 * @param entityX   Current world X of the entity.
 * @param entityY   Current world Y of the entity.
 * @param targetX   World X of the investigate target (the corpse position).
 * @param targetY   World Y of the investigate target.
 * @param detection Detection subsystem used to check LOS to the target.
 * @param logs      Accumulated log messages (mutated in place).
 * @returns The action AIComponent should execute.
 */
export function resolveCorpseArrival(
  state: CorpseInvestigationState,
  entityX: number,
  entityY: number,
  targetX: number,
  targetY: number,
  detection: Detection,
  logs: string[],
): CorpseArrivalAction {
  if (detection.checkLOS(entityX, entityY, targetX, targetY)) {
    logs.push(`INVESTIGATE arrival → corpse visible → spike aggro`);
    return { action: 'spike', x: targetX, y: targetY };
  }

  const key = corpseKey(targetX, targetY);
  state.blacklist.add(key);
  logs.push(`INVESTIGATE arrival → no LOS to corpse → resume patrol (blacklisted)`);
  return { action: 'resume', x: targetX, y: targetY };
}

/**
 * Clear the entire blacklist — called when the AI is hard-reset (e.g. player death).
 * Allows a fresh run to re-investigate corpses from a previous round.
 */
export function resetCorpseBlacklist(state: CorpseInvestigationState): void {
  state.blacklist.clear();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Stable string key for a corpse position (rounded to nearest pixel). */
function corpseKey(x: number, y: number): string {
  return `${Math.round(x)},${Math.round(y)}`;
}
